import { faSpinnerThird } from '@fortawesome/pro-regular-svg-icons';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from '@uturn/api';
import { getFetchAllGroupQueryKey, useCreateCarrierGroup } from '@uturn/api/v2';
import Overlay from '@uturn/portal/components/overlay';
import {
	type CarrierGroupFormValues,
	carrierGroupSchema,
} from '@uturn/portal/modules/carriers/schema';
import {
	Button,
	Dialog,
	DialogContent,
	DialogHeader,
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
	Icon,
	Input,
	sonner,
} from '@uturn/ui-kit';
import posthog from 'posthog-js';
import type React from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useGroupContext } from '../../context/group-context';

type Props = {
	open: boolean;
	setOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

const CreateGroup: React.FC<Props> = ({ open, setOpen }) => {
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const { mutateAsync: mutateCreateCarrierGroup, isPending } =
		useCreateCarrierGroup();

	const { setGroups } = useGroupContext();

	const form = useForm<CarrierGroupFormValues>({
		resolver: zodResolver(carrierGroupSchema),
		mode: 'onChange',
		defaultValues: {
			name: '',
		},
	});
	const onSubmitHandler = (data: CarrierGroupFormValues) => {
		mutateCreateCarrierGroup(
			{
				data: {
					groupName: data.name,
				},
			},
			{
				onSuccess: async ({ data: group }) => {
					await queryClient.refetchQueries({
						queryKey: getFetchAllGroupQueryKey(),
					});
					setGroups((state) => [
						{
							groupId: group.groupId,
							groupName: group.groupName,
							carriers: group.carriers,
						},
						...state,
					]);
					sonner.success(
						t(
							'create-group.toast.success-message',
							`Group "{{name}}" created successfully`,
							{
								name: group.groupName,
							},
						),
					);
					posthog.capture('create_carriers_group', {
						name: 'button_create_carriers_group',
						groupId: group.groupId,
						groupName: group.groupName,
						carriers: group.carriers,
					});
					setOpen(false);
					form.reset();
				},
				onError: (error) => {
					if (isAxiosError(error)) {
						if (error.response?.status === 409) {
							sonner.error(
								t(
									'create-group.toast.duplicate-error-message',
									'Group "{{name}}" already exists, please choose a different name',
									{
										name: data.name,
									},
								),
							);
							return;
						}
					}

					sonner.error(
						t(
							'toast.generic-error-message',
							'An unexpected error occurred. Please contact support',
						),
					);
				},
			},
		);
	};

	const handleOnOpenChange = (open: boolean) => {
		if (open === false) {
			form.reset();
		}

		setOpen(open);
	};

	return (
		<Dialog open={open} onOpenChange={handleOnOpenChange}>
			<DialogContent>
				{isPending && (
					<Overlay>
						<Icon
							icon={faSpinnerThird}
							spin
							className="text-primary"
							size="xl"
						/>
					</Overlay>
				)}
				<DialogHeader>
					{t('create-group.modal.title', 'Create group')}
				</DialogHeader>
				<Form {...form}>
					<form
						onSubmit={form.handleSubmit(onSubmitHandler)}
						className="flex flex-col gap-4"
					>
						<FormField
							control={form.control}
							name="name"
							render={({ field }) => (
								<FormItem>
									<FormLabel>
										{t('create-group.modal.form.name.label', 'Name')}
									</FormLabel>
									<FormControl>
										<Input {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<Button
							type="submit"
							data-usetiful="submit-create-group"
							disabled={!form.formState.isValid || isPending}
						>
							{t('create-group.modal.submit', 'Create')}
						</Button>
					</form>
				</Form>
			</DialogContent>
		</Dialog>
	);
};

export default CreateGroup;
